// src/RaportOre.js
import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDataProvider, useNotify, Title } from "react-admin";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { isColaboratorSimple, getColaboratorId, useCanManage } from "./usePermissions";

const extractId = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value.id;
    if (typeof value === "string") {
        const match = value.match(/\/(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
    }
    return value;
};

const extractIds = (collection) => (collection || []).map(extractId).filter(Boolean);

const lunaCurenta = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const descarcaFisier = (continut, numeFisier, mimeType) => {
    const blob = new Blob([continut], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = numeFisier;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const csvEscape = (val) => {
    const s = String(val ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
};

// Pe mobil tabelul centralizatorului are pana la 7 coloane si nu incape.
// Il randam ca un card per colaborator, cu perechi eticheta/valoare stivuite,
// ca sa nu fie nevoie de scroll orizontal pe teren.
const LinieValoare = ({ eticheta, children, accent }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", py: 0.35 }}>
        <Typography variant="body2" color="text.secondary">{eticheta}</Typography>
        <Typography variant="body2" sx={{ fontWeight: accent ? 700 : 500, textAlign: "right" }}>
            {children}
        </Typography>
    </Box>
);

const CardColaborator = ({ r, canManage }) => (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {r.colaborator.nume} {r.colaborator.prenume}
        </Typography>
        {r.colaborator.rol && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {r.colaborator.rol}
            </Typography>
        )}
        <Divider sx={{ my: 1 }} />
        <LinieValoare eticheta="Total ore" accent>{r.totalOre.toFixed(2)}h</LinieValoare>
        {canManage && (
            <>
                <LinieValoare eticheta="Tarif/h">
                    {r.peOra != null ? `${parseFloat(r.peOra).toFixed(2)} lei` : "—"}
                </LinieValoare>
                <LinieValoare eticheta="Total cost">
                    {r.totalCost != null ? `${r.totalCost.toFixed(2)} lei` : "—"}
                </LinieValoare>
                <LinieValoare eticheta="Avans">
                    {r.totalAvans > 0 ? `${r.totalAvans.toFixed(2)} lei` : "—"}
                </LinieValoare>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <Typography variant="body2" color="text.secondary">Rest de plata</Typography>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: r.restDePlata < 0 ? "error.main" : "success.main" }}
                    >
                        {r.restDePlata != null ? `${r.restDePlata.toFixed(2)} lei` : "—"}
                    </Typography>
                </Box>
            </>
        )}
    </Paper>
);

export const RaportOre = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const colaboratorSimple = isColaboratorSimple();
    const colaboratorId = getColaboratorId();
    const canManage = useCanManage();
    const esteMobil = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    const [lunaSelectata, setLunaSelectata] = useState(lunaCurenta());
    const [filtruEchipa, setFiltruEchipa] = useState('');
    const [filtruColaborator, setFiltruColaborator] = useState('');
    const [loading, setLoading] = useState(true);
    const [evenimente, setEvenimente] = useState([]);
    const [colaboratori, setColaboratori] = useState([]);
    const [echipe, setEchipe] = useState([]);
    const [oreEvenimente, setOreEvenimente] = useState([]);
    const [avansuri, setAvansuri] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [evRes, colabRes, echipeRes, oreRes, avansRes] = await Promise.all([
                dataProvider.getList("evenimente", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
                dataProvider.getList("colaboratori", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
                dataProvider.getList("echipe", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
                dataProvider.getList("evenimente-ore", { pagination: { page: 1, perPage: 1000 }, sort: { field: "id", order: "ASC" }, filter: {} }),
                dataProvider.getList("avansuri", { pagination: { page: 1, perPage: 1000 }, sort: { field: "data", order: "ASC" }, filter: {} }),
            ]);
            setEvenimente(evRes.data);
            setColaboratori(colabRes.data);
            setEchipe(echipeRes.data);
            setOreEvenimente(oreRes.data);
            setAvansuri(avansRes.data);
        } catch (err) {
            notify("Eroare la incarcarea raportului", { type: "error" });
        } finally {
            setLoading(false);
        }
    }, [dataProvider, notify]);

    useEffect(() => { load(); }, [load]);

    const colaboratorMap = useMemo(() => Object.fromEntries(colaboratori.map((c) => [c.id, c])), [colaboratori]);
    const echipaMap = useMemo(() => Object.fromEntries(echipe.map((e) => [e.id, e])), [echipe]);

    // Pontajul e pe zile: o inregistrare apartine lunii dupa `o.data` (ziua
    // lucrata), nu dupa luna evenimentului. Pentru evenimentele de o zi e
    // acelasi lucru; pentru cele care trec granita lunii, orele cad in luna
    // corecta. Filtrul de echipa se aplica in continuare prin evenimentul orei.
    const evenimentMap = useMemo(() => {
        const m = {};
        evenimente.forEach((ev) => { m[ev.id] = ev; });
        return m;
    }, [evenimente]);

    const oreLuna = useMemo(() => {
        let ore = oreEvenimente.filter((o) => {
            if (!o.data) return false;
            const d = new Date(o.data);
            const cheie = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            if (cheie !== lunaSelectata) return false;

            if (filtruEchipa) {
                const ev = evenimentMap[extractId(o.eveniment)];
                const echipeEv = ev ? extractIds(ev.echipe) : [];
                if (!echipeEv.includes(parseInt(filtruEchipa, 10))) return false;
            }
            return true;
        });
        if (colaboratorSimple && colaboratorId) {
            ore = ore.filter((o) => extractId(o.colaborator) === colaboratorId);
        }
        if (filtruColaborator) {
            ore = ore.filter((o) => extractId(o.colaborator) === parseInt(filtruColaborator, 10));
        }
        return ore;
    }, [oreEvenimente, evenimentMap, lunaSelectata, filtruEchipa, colaboratorSimple, colaboratorId, filtruColaborator]);

    // Socotelile se inchid la sfarsitul lunii: intra doar avansurile cu data
    // in luna selectata, iar soldul nu se reporteaza in luna urmatoare.
    const avansuriPeColaborator = useMemo(() => {
        const totaluri = {};
        avansuri.forEach((a) => {
            if (!a.data) return;
            const d = new Date(a.data);
            const cheie = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (cheie !== lunaSelectata) return;

            const colabId = extractId(a.colaborator);
            if (!colabId) return;
            if (colaboratorSimple && colaboratorId && colabId !== colaboratorId) return;
            if (filtruColaborator && colabId !== parseInt(filtruColaborator, 10)) return;

            totaluri[colabId] = (totaluri[colabId] || 0) + (parseFloat(a.suma) || 0);
        });
        return totaluri;
    }, [avansuri, lunaSelectata, colaboratorSimple, colaboratorId, filtruColaborator]);

    const centralizator = useMemo(() => {
        const totaluri = {};
        oreLuna.forEach((o) => {
            const colabId = extractId(o.colaborator);
            if (!colabId) return;
            totaluri[colabId] = (totaluri[colabId] || 0) + (o.oreConfirmate || 0);
        });

        // Un colaborator care a primit avans dar nu are ore in luna trebuie sa
        // apara oricum, altfel avansul lui dispare din raport.
        Object.keys(avansuriPeColaborator).forEach((colabId) => {
            if (!(colabId in totaluri)) totaluri[colabId] = 0;
        });

        return Object.entries(totaluri)
            .map(([colabId, total]) => {
                const colab = colaboratorMap[colabId];
                const peOra = colab?.peOra ? parseFloat(colab.peOra) : null;
                const totalOre = Math.round(total * 100) / 100;
                const totalCost = peOra != null ? Math.round(totalOre * peOra * 100) / 100 : null;
                const totalAvans = Math.round((avansuriPeColaborator[colabId] || 0) * 100) / 100;
                const restDePlata = totalCost != null ? Math.round((totalCost - totalAvans) * 100) / 100 : null;
                return { colaboratorId: parseInt(colabId, 10), colaborator: colab, totalOre, peOra, totalCost, totalAvans, restDePlata };
            })
            .filter((r) => r.colaborator)
            .sort((a, b) => b.totalOre - a.totalOre);
    }, [oreLuna, colaboratorMap, avansuriPeColaborator]);

    const totalGeneral = useMemo(
        () => Math.round(centralizator.reduce((sum, r) => sum + r.totalOre, 0) * 100) / 100,
        [centralizator]
    );

    const totalCostGeneral = useMemo(() => {
        const suma = centralizator.reduce((sum, r) => r.totalCost != null ? sum + r.totalCost : sum, 0);
        return Math.round(suma * 100) / 100;
    }, [centralizator]);

    const totalAvansGeneral = useMemo(
        () => Math.round(centralizator.reduce((sum, r) => sum + (r.totalAvans || 0), 0) * 100) / 100,
        [centralizator]
    );

    const totalRestGeneral = useMemo(() => {
        const suma = centralizator.reduce((sum, r) => r.restDePlata != null ? sum + r.restDePlata : sum, 0);
        return Math.round(suma * 100) / 100;
    }, [centralizator]);

    // Construit din oreLuna (deja filtrat pe luna dupa o.data + echipa +
    // colaborator), ca un eveniment care trece granita lunii sa apara in fiecare
    // luna doar cu orele lucrate acolo. Grupam orele lunii pe eveniment.
    const detaliiEvenimente = useMemo(() => {
        const peEveniment = {};
        oreLuna.forEach((o) => {
            const evId = extractId(o.eveniment);
            (peEveniment[evId] = peEveniment[evId] || []).push(o);
        });
        return Object.entries(peEveniment)
            .map(([evId, ore]) => {
                const ev = evenimentMap[evId];
                if (!ev) return null;
                const echipeEv = extractIds(ev.echipe).map((id) => echipaMap[id]).filter(Boolean);
                const randuriColaboratori = ore
                    .map((o) => ({ colaborator: colaboratorMap[extractId(o.colaborator)], ore: o.oreConfirmate }))
                    .filter((r) => r.colaborator);
                return { eveniment: ev, echipe: echipeEv, colaboratori: randuriColaboratori };
            })
            .filter((d) => d && d.colaboratori.length > 0)
            .sort((a, b) => new Date(a.eveniment.dataStart) - new Date(b.eveniment.dataStart));
    }, [oreLuna, evenimentMap, echipaMap, colaboratorMap]);

    const handleExportCSV = () => {
        const header = ["Eveniment", "Data", "Echipe", "Colaborator", "Ore confirmate"];
        if (canManage) header.push("Tarif/h (lei)", "Cost (lei)");
        const linii = [header];
        detaliiEvenimente.forEach((d) => {
            const numeEchipe = d.echipe.map((e) => e.nume).join("; ");
            const dataEv = new Date(d.eveniment.dataStart).toLocaleDateString("ro-RO");
            if (!d.colaboratori.length) {
                const rand = [d.eveniment.titlu, dataEv, numeEchipe, "-", "-"];
                if (canManage) rand.push("-", "-");
                linii.push(rand);
            } else {
                d.colaboratori.forEach((c) => {
                    const peOra = c.colaborator?.peOra ? parseFloat(c.colaborator.peOra) : null;
                    const cost = peOra != null ? Math.round(c.ore * peOra * 100) / 100 : null;
                    const rand = [d.eveniment.titlu, dataEv, numeEchipe, `${c.colaborator.nume} ${c.colaborator.prenume}`, c.ore];
                    if (canManage) rand.push(peOra ?? "-", cost ?? "-");
                    linii.push(rand);
                });
            }
        });
        if (canManage && centralizator.length) {
            linii.push([]);
            linii.push(["Centralizator", "Total ore", "Cost (lei)", "Avans (lei)", "Rest de plata (lei)"]);
            centralizator.forEach((r) => {
                linii.push([
                    `${r.colaborator.nume} ${r.colaborator.prenume}`,
                    r.totalOre.toFixed(2),
                    r.totalCost != null ? r.totalCost.toFixed(2) : "-",
                    r.totalAvans.toFixed(2),
                    r.restDePlata != null ? r.restDePlata.toFixed(2) : "-",
                ]);
            });
            linii.push([
                "TOTAL", totalGeneral.toFixed(2), totalCostGeneral.toFixed(2),
                totalAvansGeneral.toFixed(2), totalRestGeneral.toFixed(2),
            ]);
        }
        const csv = linii.map((linie) => linie.map(csvEscape).join(",")).join("\n");
        descarcaFisier("\uFEFF" + csv, `raport-ore-${lunaSelectata}.csv`, "text/csv;charset=utf-8");
    };

    const handleExportPDF = async () => {
        const { default: jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');

        const doc = new jsPDF();
        const luna = new Date(lunaSelectata + '-01').toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Raport Ore Lucrate', 14, 20);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(`Luna: ${luna}`, 14, 28);
        doc.text(`Generat: ${new Date().toLocaleDateString('ro-RO')}`, 14, 34);

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Centralizator ore per colaborator', 14, 44);

        const headerCentralizator = ['Colaborator', 'Rol', 'Total ore'];
        if (canManage) headerCentralizator.push('Tarif/h (lei)', 'Cost (lei)', 'Avans (lei)', 'Rest de plata (lei)');

        const rowsCentralizator = centralizator.map((r) => {
            const rand = [
                `${r.colaborator.nume} ${r.colaborator.prenume}`,
                r.colaborator.rol || '-',
                `${r.totalOre.toFixed(2)}h`,
            ];
            if (canManage) {
                rand.push(r.peOra != null ? `${parseFloat(r.peOra).toFixed(2)}` : '-');
                rand.push(r.totalCost != null ? `${r.totalCost.toFixed(2)}` : '-');
                rand.push(r.totalAvans > 0 ? `${r.totalAvans.toFixed(2)}` : '-');
                rand.push(r.restDePlata != null ? `${r.restDePlata.toFixed(2)}` : '-');
            }
            return rand;
        });

        const randTotal = ['TOTAL', '', `${totalGeneral.toFixed(2)}h`];
        if (canManage) {
            randTotal.push('', totalCostGeneral > 0 ? `${totalCostGeneral.toFixed(2)}` : '-');
            randTotal.push(totalAvansGeneral > 0 ? `${totalAvansGeneral.toFixed(2)}` : '-');
            randTotal.push(`${totalRestGeneral.toFixed(2)}`);
        }

        autoTable(doc, {
            startY: 48,
            head: [headerCentralizator],
            body: [...rowsCentralizator, randTotal],
            styles: { fontSize: 9 },
            headStyles: { fillColor: [25, 118, 210] },
            didParseCell: (data) => {
                if (data.row.index === rowsCentralizator.length) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            },
        });

        let y = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Detaliu pe evenimente', 14, y);
        y += 6;

        detaliiEvenimente.forEach((d) => {
            if (y > 250) { doc.addPage(); y = 20; }

            const dataEv = new Date(d.eveniment.dataStart).toLocaleDateString('ro-RO');
            const numeEchipe = d.echipe.map((e) => e.nume).join(', ');

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`${d.eveniment.titlu}  —  ${dataEv}${numeEchipe ? `  [${numeEchipe}]` : ''}`, 14, y);
            y += 4;

            const headerEv = ['Colaborator', 'Ore'];
            if (canManage) headerEv.push('Cost (lei)');

            const rowsEv = d.colaboratori.map((c) => {
                const peOra = c.colaborator?.peOra ? parseFloat(c.colaborator.peOra) : null;
                const cost = peOra != null ? Math.round(c.ore * peOra * 100) / 100 : null;
                const rand = [`${c.colaborator.nume} ${c.colaborator.prenume}`, `${c.ore.toFixed(2)}h`];
                if (canManage) rand.push(cost != null ? `${cost.toFixed(2)}` : '-');
                return rand;
            });

            autoTable(doc, {
                startY: y,
                head: [headerEv],
                body: rowsEv,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [66, 66, 66] },
                margin: { left: 14 },
            });

            y = doc.lastAutoTable.finalY + 6;
        });

        doc.save(`raport-ore-${lunaSelectata}.pdf`);
    };

    if (loading) {
        return <Card style={{ margin: "1em" }}><CardContent>Se incarca raportul...</CardContent></Card>;
    }

    return (
        <Card style={{ margin: "1em" }}>
            <Title title="Raport ore" />
            <CardContent>
                {colaboratorSimple && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Vizualizezi doar orele tale confirmate.
                    </Alert>
                )}

                <Box sx={{ display: "flex", gap: "4px", alignItems: "center", mb: 3, flexWrap: "wrap" }}>
                    <TextField
                        type="month" label="Luna" size="small" value={lunaSelectata}
                        onChange={(e) => setLunaSelectata(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(20% - 4px)' } }}
                    />
                    {!colaboratorSimple && (
                        <FormControl size="small" sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(20% - 4px)' } }}>
                            <InputLabel>Echipă</InputLabel>
                            <Select value={filtruEchipa} label="Echipă"
                                onChange={(e) => { setFiltruEchipa(e.target.value); setFiltruColaborator(''); }}
                            >
                                <MenuItem value="">Toate</MenuItem>
                                {echipe.map((e) => (
                                    <MenuItem key={e.id} value={String(e.id)}>{e.nume}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {!colaboratorSimple && (
                        <FormControl size="small" sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(20% - 4px)' } }}>
                            <InputLabel>Colaborator</InputLabel>
                            <Select value={filtruColaborator} label="Colaborator"
                                onChange={(e) => setFiltruColaborator(e.target.value)}
                            >
                                <MenuItem value="">Toți</MenuItem>
                                {colaboratori
                                    .filter((c) => !filtruEchipa || extractId(c.echipa) === parseInt(filtruEchipa, 10))
                                    .map((c) => (
                                        <MenuItem key={c.id} value={String(c.id)}>{c.nume} {c.prenume}</MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    )}
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV}
                        disabled={!detaliiEvenimente.length}
                        sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(20% - 4px)' }, height: 40 }}>
                        Export CSV
                    </Button>
                    <Button variant="outlined" color="error" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF}
                        disabled={!detaliiEvenimente.length}
                        sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(20% - 4px)' }, height: 40 }}>
                        Export PDF
                    </Button>
                </Box>

                <Typography variant="h6" sx={{ mb: 1 }}>
                    {colaboratorSimple ? "Totalul orelor mele" : "Centralizator ore pe colaborator"}
                </Typography>
                {!centralizator.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Nicio ora confirmata in aceasta luna.
                    </Typography>
                ) : esteMobil ? (
                    <Box sx={{ mb: 3 }}>
                        <Stack>
                            {centralizator.map((r) => (
                                <CardColaborator key={r.colaboratorId} r={r} canManage={canManage} />
                            ))}
                        </Stack>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Total {centralizator.length === 1 ? "(1 colaborator)" : `(${centralizator.length} colaboratori)`}
                            </Typography>
                            <LinieValoare eticheta="Total ore" accent>{totalGeneral.toFixed(2)}h</LinieValoare>
                            {canManage && (
                                <>
                                    <LinieValoare eticheta="Total cost">
                                        {totalCostGeneral > 0 ? `${totalCostGeneral.toFixed(2)} lei` : "—"}
                                    </LinieValoare>
                                    <LinieValoare eticheta="Avans">
                                        {totalAvansGeneral > 0 ? `${totalAvansGeneral.toFixed(2)} lei` : "—"}
                                    </LinieValoare>
                                    <LinieValoare eticheta="Rest de plata" accent>
                                        {totalRestGeneral.toFixed(2)} lei
                                    </LinieValoare>
                                </>
                            )}
                        </Paper>
                    </Box>
                ) : (
                    <Table size="small" sx={{ mb: 3 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Colaborator</TableCell>
                                <TableCell>Rol</TableCell>
                                <TableCell align="right">Total ore</TableCell>
                                {canManage && <TableCell align="right">Tarif/h</TableCell>}
                                {canManage && <TableCell align="right">Total cost (lei)</TableCell>}
                                {canManage && <TableCell align="right">Avans (lei)</TableCell>}
                                {canManage && <TableCell align="right">Rest de plata (lei)</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {centralizator.map((r) => (
                                <TableRow key={r.colaboratorId}>
                                    <TableCell>{r.colaborator.nume} {r.colaborator.prenume}</TableCell>
                                    <TableCell><Typography variant="caption" color="text.secondary">{r.colaborator.rol || "-"}</Typography></TableCell>
                                    <TableCell align="right"><strong>{r.totalOre.toFixed(2)}h</strong></TableCell>
                                    {canManage && <TableCell align="right">{r.peOra != null ? `${parseFloat(r.peOra).toFixed(2)} lei` : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>}
                                    {canManage && <TableCell align="right">{r.totalCost != null ? <strong>{r.totalCost.toFixed(2)} lei</strong> : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>}
                                    {canManage && <TableCell align="right">{r.totalAvans > 0 ? `${r.totalAvans.toFixed(2)} lei` : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>}
                                    {canManage && (
                                        <TableCell align="right">
                                            {r.restDePlata != null ? (
                                                <strong style={{ color: r.restDePlata < 0 ? "#d32f2f" : undefined }}>
                                                    {r.restDePlata.toFixed(2)} lei
                                                </strong>
                                            ) : <span style={{ opacity: 0.4 }}>—</span>}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: "action.hover" }}>
                                <TableCell colSpan={2}><strong>Total</strong></TableCell>
                                <TableCell align="right"><strong>{totalGeneral.toFixed(2)}h</strong></TableCell>
                                {canManage && <TableCell />}
                                {canManage && <TableCell align="right"><strong>{totalCostGeneral > 0 ? `${totalCostGeneral.toFixed(2)} lei` : "—"}</strong></TableCell>}
                                {canManage && <TableCell align="right"><strong>{totalAvansGeneral > 0 ? `${totalAvansGeneral.toFixed(2)} lei` : "—"}</strong></TableCell>}
                                {canManage && <TableCell align="right"><strong>{totalRestGeneral.toFixed(2)} lei</strong></TableCell>}
                            </TableRow>
                        </TableBody>
                    </Table>
                )}

                <Divider sx={{ mb: 2 }} />

                <Typography variant="h6" sx={{ mb: 1 }}>
                    {colaboratorSimple ? "Evenimentele mele" : "Detaliu pe evenimente"}
                </Typography>
                {!detaliiEvenimente.length ? (
                    <Typography variant="body2" color="text.secondary">
                        Niciun eveniment cu ore confirmate in aceasta luna.
                    </Typography>
                ) : (
                    detaliiEvenimente.map((d) => (
                        <Accordion key={d.eveniment.id}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                    <Typography sx={{ fontWeight: 500 }}>{d.eveniment.titlu}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(d.eveniment.dataStart).toLocaleDateString("ro-RO")}
                                    </Typography>
                                    {canManage && (() => {
                                        const totalEv = d.colaboratori.reduce((sum, c) => {
                                            const peOra = c.colaborator?.peOra ? parseFloat(c.colaborator.peOra) : null;
                                            return peOra != null ? sum + Math.round(c.ore * peOra * 100) / 100 : sum;
                                        }, 0);
                                        const totalMembrii = d.echipe.reduce((sum, e) => {
                                            const membrii = colaboratori.filter(c => {
                                                const echId = typeof c.echipa === 'object' ? c.echipa?.id : extractId(c.echipa);
                                                return echId === e.id;
                                            });
                                            return sum + membrii.length;
                                        }, 0);
                                        const confirmati = d.colaboratori.length;
                                        const procent = totalMembrii > 0 ? Math.round(confirmati / totalMembrii * 100) : 0;
                                        const culoare = procent === 100 ? 'success.main' : procent >= 50 ? 'warning.main' : 'error.main';
                                        return (
                                            <>
                                                {totalEv > 0 && (
                                                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                                        {(Math.round(totalEv * 100) / 100).toFixed(2)} lei
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color={culoare} sx={{ fontWeight: 600 }}>
                                                    {confirmati}/{totalMembrii} ({procent}%)
                                                </Typography>
                                            </>
                                        );
                                    })()}
                                    {d.echipe.map((e) => (
                                        <Chip key={e.id} label={e.nume} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Colaborator</TableCell>
                                            <TableCell align="right">Ore confirmate</TableCell>
                                            {canManage && <TableCell align="right">Cost (lei)</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {d.colaboratori.map((c, idx) => {
                                            const peOra = c.colaborator?.peOra ? parseFloat(c.colaborator.peOra) : null;
                                            const cost = peOra != null ? Math.round(c.ore * peOra * 100) / 100 : null;
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell>{c.colaborator.nume} {c.colaborator.prenume}</TableCell>
                                                    <TableCell align="right">{c.ore.toFixed(2)}h</TableCell>
                                                    {canManage && <TableCell align="right">{cost != null ? `${cost.toFixed(2)} lei` : <span style={{ opacity: 0.4 }}>—</span>}</TableCell>}
                                                </TableRow>
                                            );
                                        })}
                                        {canManage && (() => {
                                            const totalEv = d.colaboratori.reduce((sum, c) => {
                                                const peOra = c.colaborator?.peOra ? parseFloat(c.colaborator.peOra) : null;
                                                return peOra != null ? sum + Math.round(c.ore * peOra * 100) / 100 : sum;
                                            }, 0);
                                            return totalEv > 0 ? (
                                                <TableRow sx={{ bgcolor: "action.hover" }}>
                                                    <TableCell colSpan={2}><strong>Total eveniment</strong></TableCell>
                                                    <TableCell align="right"><strong>{(Math.round(totalEv * 100) / 100).toFixed(2)} lei</strong></TableCell>
                                                </TableRow>
                                            ) : null;
                                        })()}
                                    </TableBody>
                                </Table>
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </CardContent>
        </Card>
    );
};
